// Machines: computers the operator connects so agents can run commands on
// them through Corvus (docs/machine-connections.md §7, §16). Paired machines
// (a small bridge that dials out) and machines reached over SSH share one
// list, one command log and one approval flow. Operator routes only: none of
// this is in the iPhone app's contract.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch, errorText } from "@/lib/api";
import type { components } from "@/types/api.gen";

type S = components["schemas"];
export type Machine = S["MachineConnectionOut"];
export type MachineList = S["MachineListOut"];
export type MachineSwitch = S["MachineSwitchOut"];
export type MachineCommand = S["MachineCommandOut"];
export type MachineDetail = S["MachineDetailOut"];
export type MachineApproval = S["MachineApprovalOut"];
export type MachineTier = S["MachineTierBody"]["tier"];
export type MachinePairBody = S["MachinePairBody"];
export type MachinePairOut = S["MachinePairOut"];
export type MachinePairCode = S["MachinePairCodeOut"];
export type MachinePairStatus = S["MachinePairStatusOut"];
export type MachineListsBody = S["MachineListsBody"];
export type MachineRotateBody = S["MachineRotateBody"];
export type SshAddressOut = S["SshAddressOut"];
export type SshAddBody = S["SshAddBody"];
export type SshAddOut = S["SshAddOut"];
export type SshTestOut = S["SshTestOut"];
export type SshLoginBody = S["SshLoginBody"];
export type SshLoginOut = S["SshLoginOut"];
export type SshRefetchOut = S["SshRefetchOut"];
export type MachineAllowedHosts = S["MachineAllowedHostsOut"];

/** What the rotate route answers: `state` add_line (a new key's line to add, then Rotate again) or rotated. */
export interface MachineRotateResult {
  ok?: boolean;
  state?: string;
  public_line?: string;
  message?: string;
}

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/machines`;
const one = (c: string, lmc: string) => `${base(c)}/${encodeURIComponent(lmc)}`;

export const machineKeys = {
  all: (c: string | null) => ["machines", c] as const,
  list: (c: string | null) => ["machines", c, "list"] as const,
  switch: (c: string | null) => ["machines", c, "switch"] as const,
  detail: (c: string | null, lmc: string | null) => ["machines", c, "detail", lmc] as const,
  commands: (c: string | null, lmc: string | null) => ["machines", c, "commands", lmc ?? "*"] as const,
  command: (c: string | null, lmx: string | null) => ["machines", c, "command", lmx] as const,
  pairing: (c: string | null, lmc: string | null) => ["machines", c, "pairing", lmc] as const,
  approvals: (c: string | null) => ["machines", c, "approvals"] as const,
  approval: (c: string | null, lma: string | null) => ["machines", c, "approval", lma] as const,
  allowedHosts: (c: string | null) => ["machines", c, "allowed-hosts"] as const,
};

function refreshAll(qc: QueryClient, c: string) {
  void qc.invalidateQueries({ queryKey: machineKeys.all(c) });
}

/** The server's refusal code on these routes (`detail.code`), else `detail.error`. */
export function machineErrorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const body = e.body as { detail?: Record<string, unknown> } | null | undefined;
  const d = body?.detail;
  if (d && typeof d === "object") {
    if (typeof d.code === "string") return d.code;
    if (typeof d.error === "string") return d.error;
  }
  return null;
}

/**
 * The sentence to show for a failure on these routes: the server's own
 * plain words (`detail.message`, written for the operator) whatever the
 * status, since its refusals use 403, 429 and 502 for things errorText would
 * turn into a stock line; errorText for a network failure, a 401 or a body
 * without them.
 */
export function machineErrorText(e: unknown): string {
  if (e instanceof ApiError && e.status !== 0 && e.status !== 401) {
    const d = (e.body as { detail?: Record<string, unknown> } | null | undefined)?.detail;
    if (d && typeof d === "object" && typeof d.code === "string" && typeof d.message === "string" && d.message.trim()) {
      return d.message.trim();
    }
  }
  return errorText(e);
}

/** A field the refusal carries besides its code and message (`address`, `can_allow`, `public_line`). */
export function machineErrorField(e: unknown, key: string): unknown {
  if (!(e instanceof ApiError)) return undefined;
  const d = (e.body as { detail?: Record<string, unknown> } | null | undefined)?.detail;
  return d && typeof d === "object" ? d[key] : undefined;
}

// ─── the switch and the list ─────────────────────────────────────────────

export function useMachineSwitch(c: string | null) {
  return useQuery({
    queryKey: machineKeys.switch(c),
    enabled: !!c,
    queryFn: ({ signal }) => apiFetch<MachineSwitch>(`${base(c!)}/switch`, { signal }),
    staleTime: 30_000,
  });
}

export function useSetMachineSwitch() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, on }: { customerId: string; on: boolean }) =>
      apiFetch<MachineSwitch>(`${base(customerId)}/switch`, { method: "PUT", body: { on } }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

const WAITING = new Set(["pending", "claimed"]);

export function useMachines(c: string | null) {
  return useQuery({
    queryKey: machineKeys.list(c),
    enabled: !!c,
    queryFn: ({ signal }) => apiFetch<MachineList>(base(c!), { signal }),
    // Faster while a pairing waits for its claim or the operator's confirmation.
    refetchInterval: (q) => (q.state.data?.machines.some((m) => WAITING.has(m.status)) ? 3_000 : 20_000),
  });
}

export function useMachine(c: string | null, lmc: string | null) {
  return useQuery({
    queryKey: machineKeys.detail(c, lmc),
    enabled: !!c && !!lmc,
    queryFn: ({ signal }) => apiFetch<MachineDetail>(one(c!, lmc!), { signal }),
    refetchInterval: 10_000,
  });
}

const RUNNING = new Set(["queued", "dispatched", "approved", "awaiting_approval"]);

/** One machine's command log, or every machine's (`lmc` null). */
export function useMachineCommands(c: string | null, lmc: string | null) {
  return useQuery({
    queryKey: machineKeys.commands(c, lmc),
    enabled: !!c,
    queryFn: ({ signal }) =>
      apiFetch<S["MachineCommandsOut"]>(lmc ? `${one(c!, lmc)}/commands` : `${base(c!)}/commands`, { signal }),
    refetchInterval: (q) => (q.state.data?.commands.some((x) => RUNNING.has(x.status)) ? 3_000 : 15_000),
  });
}

/** One command with the output Corvus kept. */
export function useMachineCommand(c: string | null, lmx: string | null) {
  return useQuery({
    queryKey: machineKeys.command(c, lmx),
    enabled: !!c && !!lmx,
    queryFn: ({ signal }) => apiFetch<MachineCommand>(`${base(c!)}/commands/${encodeURIComponent(lmx!)}`, { signal }),
    refetchInterval: (q) => (q.state.data && RUNNING.has(q.state.data.status) ? 2_000 : false),
  });
}

export function useStopMachineCommand() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmx }: { customerId: string; lmx: string }) =>
      apiFetch<MachineCommand>(`${base(customerId)}/commands/${encodeURIComponent(lmx)}/stop`, { method: "POST" }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

// ─── one machine's settings ──────────────────────────────────────────────

function machineMutation<B>(path: (lmc: string) => string, method: "POST" | "PUT" | "DELETE" = "POST") {
  return function useIt() {
    const qc = useQueryClient();
    return useMutation({
      meta: { errorHandled: true },
      mutationFn: ({ customerId, lmc, body }: { customerId: string; lmc: string; body?: B }) =>
        apiFetch<Machine>(`${one(customerId, lmc)}${path(lmc)}`, { method, body }),
      onSuccess: (_, v) => refreshAll(qc, v.customerId),
    });
  };
}

export const useSetMachineTier = machineMutation<S["MachineTierBody"]>(() => "/tier");
export const useSetMachineScope = machineMutation<S["MachineScopeBody"]>(() => "/scope");
export const useSetMachineLists = machineMutation<MachineListsBody>(() => "/lists", "PUT");
export const useRevokeMachine = machineMutation<never>(() => "", "DELETE");
export const useConfirmMachine = machineMutation<never>(() => "/confirm");
export const useRejectMachine = machineMutation<never>(() => "/reject");

export function useRotateMachine() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmc, body }: { customerId: string; lmc: string; body: MachineRotateBody }) =>
      apiFetch<MachineRotateResult>(`${one(customerId, lmc)}/rotate`, { method: "POST", body }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

// ─── pairing ─────────────────────────────────────────────────────────────

export function usePairMachine() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, body }: { customerId: string; body: MachinePairBody }) =>
      apiFetch<MachinePairOut>(`${base(customerId)}/pair`, { method: "POST", body }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

/** The pairing as the dialog shows it while it waits (polled). */
export function useMachinePairing(c: string | null, lmc: string | null) {
  return useQuery({
    queryKey: machineKeys.pairing(c, lmc),
    enabled: !!c && !!lmc,
    queryFn: ({ signal }) => apiFetch<MachinePairStatus>(`${one(c!, lmc!)}/pair`, { signal }),
    refetchInterval: (q) => (q.state.data && WAITING.has(q.state.data.status) ? 2_000 : false),
  });
}

export function useNewPairCode() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmc }: { customerId: string; lmc: string }) =>
      apiFetch<MachinePairCode>(`${one(customerId, lmc)}/pair/code`, { method: "POST" }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

// ─── SSH ─────────────────────────────────────────────────────────────────

/** Check the address and fetch the host key, with no credentials sent. Nothing is stored. */
export function useCheckSshAddress() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, host, port }: { customerId: string; host: string; port: number }) =>
      apiFetch<SshAddressOut>(`${base(customerId)}/ssh/hostkey`, { method: "POST", body: { host, port } }),
  });
}

export function useAddSshMachine() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, body }: { customerId: string; body: SshAddBody }) =>
      apiFetch<SshAddOut>(`${base(customerId)}/ssh`, { method: "POST", body }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

export function useTestSshMachine() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmc }: { customerId: string; lmc: string }) =>
      apiFetch<SshTestOut>(`${base(customerId)}/ssh/${encodeURIComponent(lmc)}/test`, { method: "POST" }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

/** Correct the address, the account or the login of a machine that has not passed its test yet. */
export function useSetSshLogin() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmc, body }: { customerId: string; lmc: string; body: SshLoginBody }) =>
      apiFetch<SshLoginOut>(`${base(customerId)}/ssh/${encodeURIComponent(lmc)}/login`, { method: "PUT", body }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

/** Fetch the host key the machine shows now (after a change, or before re-pinning). */
export function useRefetchSshHostKey() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmc }: { customerId: string; lmc: string }) =>
      apiFetch<SshRefetchOut>(`${base(customerId)}/ssh/${encodeURIComponent(lmc)}/hostkey`, { method: "POST" }),
  });
}

/** Confirm (first pin) or re-pin the key Corvus fetched, by its fingerprint. */
export function usePinSshHostKey() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lmc, fingerprint, repin }: { customerId: string; lmc: string; fingerprint: string; repin: boolean }) =>
      apiFetch<Machine>(`${base(customerId)}/ssh/${encodeURIComponent(lmc)}/${repin ? "repin" : "hostkey/confirm"}`, {
        method: "POST",
        body: { fingerprint },
      }),
    onSuccess: (_, v) => refreshAll(qc, v.customerId),
  });
}

export function useMachineAllowedHosts(c: string | null) {
  return useQuery({
    queryKey: machineKeys.allowedHosts(c),
    enabled: !!c,
    queryFn: ({ signal }) => apiFetch<MachineAllowedHosts>(`${base(c!)}/allowed-hosts`, { signal }),
  });
}

export function useSetMachineAllowedHosts() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, hosts }: { customerId: string; hosts: string[] }) =>
      apiFetch<MachineAllowedHosts>(`${base(customerId)}/allowed-hosts`, { method: "PUT", body: { hosts } }),
    onSuccess: (_, v) => void qc.invalidateQueries({ queryKey: machineKeys.allowedHosts(v.customerId) }),
  });
}

// ─── approvals ───────────────────────────────────────────────────────────

export function useMachineApprovals(c: string | null) {
  return useQuery({
    queryKey: machineKeys.approvals(c),
    enabled: !!c,
    queryFn: ({ signal }) => apiFetch<S["MachineApprovalsOut"]>(`${base(c!)}/approvals`, { signal }),
    refetchInterval: 15_000,
  });
}

export function useMachineApproval(c: string | null, lma: string | null) {
  return useQuery({
    queryKey: machineKeys.approval(c, lma),
    enabled: !!c && !!lma,
    queryFn: ({ signal }) => apiFetch<MachineApproval>(`${base(c!)}/approvals/${encodeURIComponent(lma!)}`, { signal }),
    refetchInterval: (q) => (q.state.data?.state === "required" ? 10_000 : false),
  });
}

/** Approve or deny, bound to the fingerprint the page showed (409 changed_meanwhile when it moved). */
export function useDecideMachineApproval() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, lma, decision, fingerprint }: { customerId: string; lma: string; decision: "approve" | "deny"; fingerprint: string }) =>
      apiFetch<MachineApproval>(`${base(customerId)}/approvals/${encodeURIComponent(lma)}/${decision}`, {
        method: "POST",
        body: { fingerprint },
      }),
    onSettled: (_, __, v) => {
      refreshAll(qc, v.customerId);
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
